/**
 * Sprint 11 — Client report email gate + optional 6-digit OTC step.
 *
 * Flow:
 *   1. User enters email → POST /verify
 *   2a. state == 'ok'       → stash email + navigate to /report
 *   2b. state == 'needs_otc' → POST /otc/request → show OTC entry → POST /otc/verify
 *   2c. otherwise            → surface friendly error notice
 */
import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { v1Post } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { ShieldCheck, AlertTriangle, XCircle, KeyRound, RefreshCcw } from 'lucide-react';
import { toast } from 'sonner';
import { toastEmailDispatch } from '@/lib/emailDispatchToast';
import { HireIqMark } from '@/components/HireIqLogo';

const ERRORS = {
  email_mismatch: { icon: AlertTriangle, tone: 'amber', body: 'The email you entered does not match the one this link was sent to.' },
  expired:        { icon: XCircle,        tone: 'rose',  body: 'This link has expired. Please contact Kadel Labs HR for a refreshed link.' },
  revoked:        { icon: XCircle,        tone: 'rose',  body: 'This link has been revoked.' },
  invalid_token:  { icon: XCircle,        tone: 'rose',  body: 'This link is invalid.' },
};

const OTC_ERRORS = {
  invalid:       'Incorrect code. Please try again.',
  expired:       'This code has expired — request a new one.',
  exhausted:     'Too many attempts. Please request a new code.',
  not_requested: 'No active code found. Please request a new one.',
};

export default function ClientGate() {
  const { token } = useParams();
  const navigate = useNavigate();

  // Step 1: email
  const [email, setEmail] = useState('');
  const [emailState, setEmailState] = useState(null);
  const [verifying, setVerifying] = useState(false);

  // Step 2: OTC
  const [otcStage, setOtcStage] = useState(false);
  const [otcTtl, setOtcTtl] = useState(null);
  const [code, setCode] = useState('');
  const [otcState, setOtcState] = useState(null);
  const [otcWorking, setOtcWorking] = useState(false);

  const requestOtc = async (emailValue) => {
    setOtcWorking(true);
    try {
      const res = await v1Post(`/client-report/${token}/otc/request`, { email: emailValue });
      if (res.state === 'sent') {
        setOtcTtl(res.ttl_minutes || 10);
        toastEmailDispatch(res.email_dispatch, {
          sentMessage: `Code emailed to ${emailValue}.`,
        });
      } else if (ERRORS[res.state]) {
        setEmailState(res.state);
        setOtcStage(false);
      } else {
        toast.error(`Could not send code: ${res.state}`);
      }
    } catch {
      toast.error('Could not request OTC.');
    } finally {
      setOtcWorking(false);
    }
  };

  const submitEmail = async () => {
    setVerifying(true);
    setEmailState(null);
    try {
      const res = await v1Post(`/client-report/${token}/verify`, { email });
      if (res.state === 'ok') {
        sessionStorage.setItem(`client-${token}`, email);
        sessionStorage.removeItem(`client-${token}-otc`);
        navigate(`/client/${token}/report`);
      } else if (res.state === 'needs_otc') {
        setOtcStage(true);
        await requestOtc(email);
      } else {
        setEmailState(res.state);
      }
    } catch {
      setEmailState('invalid_token');
    } finally {
      setVerifying(false);
    }
  };

  const submitOtc = async () => {
    setOtcWorking(true);
    setOtcState(null);
    try {
      const res = await v1Post(`/client-report/${token}/otc/verify`, { email, code });
      if (res.state === 'ok') {
        sessionStorage.setItem(`client-${token}`, email);
        sessionStorage.setItem(`client-${token}-otc`, 'verified');
        navigate(`/client/${token}/report`);
      } else {
        setOtcState(res.state);
      }
    } catch {
      setOtcState('invalid');
    } finally {
      setOtcWorking(false);
    }
  };

  return (
    <div className="hero-glow min-h-screen">
      <div className="mx-auto flex min-h-screen max-w-xl items-center justify-center px-6 py-12">
        <Card className="card-elevated w-full">
          <CardContent className="p-8">
            <div className="flex items-center gap-3">
              <HireIqMark size="md" data-testid="client-gate-brand-mark" />
              <div>
                <p className="text-xs uppercase tracking-wider text-muted-foreground">Kadel Labs</p>
                <h1 className="text-xl font-semibold">Candidate Profile</h1>
              </div>
            </div>

            {!otcStage && (
              <>
                <p className="mt-4 text-sm text-muted-foreground">
                  For your privacy, please confirm the email address that received this link.
                </p>
                {emailState && ERRORS[emailState] && (
                  <Notice
                    icon={ERRORS[emailState].icon}
                    colour={ERRORS[emailState].tone}
                    body={ERRORS[emailState].body}
                  />
                )}
                <div className="mt-5 space-y-3">
                  <div>
                    <label className="text-xs font-medium text-muted-foreground">Your email</label>
                    <Input
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="you@company.com"
                      data-testid="client-gate-email-input"
                    />
                  </div>
                  <Button
                    disabled={verifying || !email}
                    onClick={submitEmail}
                    className="w-full gap-1.5"
                    data-testid="client-gate-submit-button"
                  >
                    <ShieldCheck className="h-4 w-4" /> {verifying ? 'Verifying…' : 'Continue'}
                  </Button>
                  <p className="text-center text-[11px] text-muted-foreground">
                    Each access is logged. Reports are read-only.
                  </p>
                </div>
              </>
            )}

            {otcStage && (
              <>
                <p className="mt-4 text-sm text-muted-foreground">
                  As an extra security step, we've emailed a 6-digit code to <strong>{email}</strong>.
                  Enter it below to view the report.
                </p>
                {otcState && OTC_ERRORS[otcState] && (
                  <Notice icon={AlertTriangle} colour="amber" body={OTC_ERRORS[otcState]} />
                )}
                <div className="mt-5 space-y-3">
                  <div>
                    <label className="text-xs font-medium text-muted-foreground">6-digit code</label>
                    <Input
                      value={code}
                      onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      placeholder="000000"
                      className="text-center font-mono text-2xl tracking-[0.4em]"
                      data-testid="client-gate-otc-input"
                      inputMode="numeric"
                      autoComplete="one-time-code"
                      maxLength={6}
                    />
                    {otcTtl != null && (
                      <p className="mt-1 text-[11px] text-muted-foreground">
                        Code expires in approximately {otcTtl} minutes.
                      </p>
                    )}
                  </div>
                  <Button
                    disabled={otcWorking || code.length < 6}
                    onClick={submitOtc}
                    className="w-full gap-1.5"
                    data-testid="client-gate-otc-submit-button"
                  >
                    <KeyRound className="h-4 w-4" /> {otcWorking ? 'Verifying…' : 'Verify code'}
                  </Button>
                  <div className="flex items-center justify-between">
                    <button
                      type="button"
                      onClick={() => { setOtcStage(false); setOtcState(null); setCode(''); }}
                      className="text-[11px] text-muted-foreground underline-offset-4 hover:underline"
                      data-testid="client-gate-otc-back-button"
                    >
                      Use a different email
                    </button>
                    <button
                      type="button"
                      onClick={() => requestOtc(email)}
                      disabled={otcWorking}
                      className="inline-flex items-center gap-1 text-[11px] text-primary underline-offset-4 hover:underline"
                      data-testid="client-gate-otc-resend-button"
                    >
                      <RefreshCcw className="h-3 w-3" /> Resend code
                    </button>
                  </div>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function Notice({ icon: Icon, colour, body }) {
  const c = {
    rose: 'border-rose-200 bg-rose-50 text-rose-700',
    amber: 'border-amber-200 bg-amber-50 text-amber-800',
  }[colour];
  return (
    <div className={`mt-4 flex items-start gap-2 rounded-lg border p-3 text-xs ${c}`}>
      <Icon className="h-4 w-4" />
      <span>{body}</span>
    </div>
  );
}
